import { SbbLoadingIndicatorCircle } from "@sbb-esta/lyne-react/loading-indicator-circle";
import { DisplayFileSize } from "components/DisplayFields/DisplayFileSize";
import { LyneButton } from "components/Lyne/LyneButton";
import { SlidingPanel } from "components/SlidingPanel/SlidingPanel";
import { LanguageEnum } from "enums/LanguageEnum";
import { useTranslations } from "providers";
import React, { useRef, useState } from "react";

import { useBoardTemplates } from "../useBoardTemplates";

export const BoardTemplatesPanel: React.FC = () => {
  const i18n = useTranslations();
  const { isLoading, results, validNames, closePanel, upload, remove, isUploading, deletingPath } = useBoardTemplates();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    upload(file, selectedLanguage || undefined).then(() => {
      setSelectedLanguage("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    });
  };

  return (
    <SlidingPanel
      isOpen={true}
      onCancel={closePanel}
      onClose={closePanel}
      header={<h1>{i18n.BoardTemplates_Title}</h1>}
      footer={
        <LyneButton onClick={closePanel} kind="ghost">
          {i18n.Actions_Close}
        </LyneButton>
      }
    >
      {isLoading && (
        <div className="py-3 d-flex justify-content-center">
          <SbbLoadingIndicatorCircle />
        </div>
      )}

      {!isLoading && (
        <table className="table table-sm sbb-table-bs table-hover">
          <thead>
            <tr>
              <th>{i18n.Name}</th>
              <th>{i18n.Language}</th>
              <th>{i18n.FileSize}</th>
              <th className="actions">{i18n.Actions}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((t) => (
              <tr key={t.path}>
                <td>{t.fileName ?? t.path}</td>
                <td>{t.language}</td>
                <td>
                  <DisplayFileSize value={t.fileSize} />
                </td>
                <td className="actions w-auto text-nowrap">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger"
                    disabled={deletingPath === t.path}
                    onClick={() => remove(t.path)}
                  >
                    {deletingPath === t.path ? (
                      <SbbLoadingIndicatorCircle />
                    ) : (
                      <i className="ms-Icon ms-Icon--Delete" />
                    )}{" "}
                    {i18n.Actions_Delete}
                  </button>
                </td>
              </tr>
            ))}
            {results.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted">
                  {i18n.EmptyTable}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Upload form */}
      <div className="mt-4 border-top pt-3">
        <h6>{i18n.BoardTemplate_UploadTitle}</h6>
        <div className="d-flex gap-2 align-items-end flex-wrap mb-3">
          <div>
            <label className="form-label small mb-1">{i18n.Language}</label>
            <select
              className="form-select form-select-sm"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              disabled={isUploading}
            >
              <option value="">{i18n.OutputDocument_Language_All}</option>
              {Object.values(LanguageEnum).map((lang) => (
                <option key={lang} value={lang}>
                  {lang.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">{i18n.File}</label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control form-control-sm"
              disabled={isUploading}
              onChange={handleFileChange}
            />
          </div>
          {isUploading && <SbbLoadingIndicatorCircle />}
        </div>

        {/* Valid file names hint */}
        {validNames.length > 0 && (
          <div className="text-muted small">
            <div className="mb-1">{i18n.Templates_ValidFileNames}:</div>
            <ul className="mb-0 ps-3">
              {validNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SlidingPanel>
  );
};
